import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { topicSharingService } from "@/services/topicSharingService";
import { Copy, Check, Share2, X } from "lucide-react";
import type { TopicShare } from "@shared/schema";

interface ShareTopicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topicId: string;
  topicName: string;
}

export function ShareTopicDialog({ open, onOpenChange, topicId, topicName }: ShareTopicDialogProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [accessType, setAccessType] = useState<"restricted" | "anyone" | "organization">("restricted");

  const { data: existingShare, refetch: refetchShare } = useQuery<TopicShare | null>({
    queryKey: ["/api/topics", topicId, "share"],
    queryFn: async () => {
      return await topicSharingService.getShareLink(topicId);
    },
    enabled: open && !!topicId,
  });

  const createShareMutation = useMutation({
    mutationFn: async () => {
      return await topicSharingService.createShareLink({
        topicId,
        accessType,
      });
    },
    onSuccess: () => {
      refetchShare();
      toast({
        title: "Share link created",
        description: "Your shareable link has been generated",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create share link",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: async () => {
      return await topicSharingService.revokeShareLink(topicId);
    },
    onSuccess: () => {
      refetchShare();
      toast({
        title: "Share link revoked",
        description: "The share link has been deactivated",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to revoke share link",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const shareLink = existingShare
    ? `${window.location.origin}/topics/share/${existingShare.shareToken}`
    : null;

  const handleCopyLink = async () => {
    if (!shareLink) {
      toast({
        title: "No link available",
        description: "Please create a share link first",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast({
        title: "Link copied!",
        description: "Share link has been copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually",
        variant: "destructive",
      });
    }
  };

  const handleCreateLink = () => {
    createShareMutation.mutate();
  };

  const handleRevokeLink = () => {
    revokeShareMutation.mutate();
  };

  useEffect(() => {
    if (existingShare) {
      setAccessType((existingShare.accessType as string) as "restricted" | "anyone" | "organization");
    }
  }, [existingShare]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Topic
          </DialogTitle>
          <DialogDescription>
            Share "{topicName}" with others via a unique link
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!shareLink ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="access-type">Access Type</Label>
                <Select value={accessType} onValueChange={(v) => setAccessType(v as any)}>
                  <SelectTrigger id="access-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">Restricted (Invite Only)</SelectItem>
                    <SelectItem value="anyone">Anyone with Link</SelectItem>
                    <SelectItem value="organization">Organization Only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {accessType === "restricted" && "Users must accept invitation to access"}
                  {accessType === "anyone" && "Anyone with the link can access"}
                  {accessType === "organization" && "Only organization members can access"}
                </p>
              </div>

              <Button
                onClick={handleCreateLink}
                disabled={createShareMutation.isPending}
                className="w-full"
                data-testid="button-create-share-link-topic"
              >
                {createShareMutation.isPending ? "Creating..." : "Generate Share Link"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Shareable Link</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={shareLink}
                    readOnly
                    className="flex-1 font-mono text-sm"
                    data-testid="input-share-link-topic"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    data-testid="button-copy-link-topic"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Copy this link and share it with others
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="access-type-edit">Access Type</Label>
                <Select
                  value={accessType}
                  onValueChange={(v) => {
                    setAccessType(v as any);
                    createShareMutation.mutate();
                  }}
                >
                  <SelectTrigger id="access-type-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">Restricted (Invite Only)</SelectItem>
                    <SelectItem value="anyone">Anyone with Link</SelectItem>
                    <SelectItem value="organization">Organization Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={handleRevokeLink}
                  disabled={revokeShareMutation.isPending}
                  className="flex-1"
                  data-testid="button-revoke-link-topic"
                >
                  <X className="h-4 w-4 mr-2" />
                  {revokeShareMutation.isPending ? "Revoking..." : "Revoke Link"}
                </Button>
                <Button
                  onClick={handleCopyLink}
                  className="flex-1"
                  data-testid="button-copy-link-full-topic"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copied ? "Copied!" : "Copy Link"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
